import { Download } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import Breadcrumbs from '/@/components/bayer/breadcrumbs';
import ModalV2 from '/@/components/bayer/modal-v2';
import PageContainer from '/@/components/bayer/page-container';
import SelectionListItem from '/@/components/bayer/selection-list-item';
import Icon from '/@/components/icon';
import TechnologySlider from '/@/components/technology-slider';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useCatalogProductById } from '/@/hooks/catalog';
import { useCPSimplifiedUseRecommendations } from '/@/hooks/crop-protection';
import { useHasWriteAccess } from '/@/hooks/session';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { getAppInstance, getCornRecommendationEnabled } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import type { RecommendationOption } from '../../dashboard/rv/components/create-recommendation-modal';
import { parseProductForProductCard } from '../components/product-card';
import CropProtectionWhenToApply from '../crop-protection/when-to-apply';
import { useSearchedProducts } from '../hooks/searched-products';
import { PRODUCT_EXPLORATION_TOUR_IDS } from '../tour/product-exploration-tour-ids';
import ProductExplorationFeatures from './components/features';
import ProductExplorationGeneral from './components/general';
import ProductExplorationH2H, { useH2HWrapper } from './components/h2h';
import ProductExplorationMetrics from './components/metrics';

const useSaveLastViewedProduct = ({ type, slug, productId }: { type: string; slug: string; productId: number }) => {
    const { saveSearchedProduct } = useSearchedProducts();
    useEffect(() => {
        saveSearchedProduct({ type, slug, productId });
    }, [type, slug, productId]);
};

export type TTab = 'main' | 'h2h' | 'features' | 'metrics';

const ProductRecommendation = ({
    catalogName,
    hasPositioning,
    hasDensityCoefficients,
}: {
    catalogName: string;
    hasPositioning: boolean;
    hasDensityCoefficients: boolean;
}) => {
    const navigate = useNavigate();
    const [selectedItem, setSelectedItem] = useState<number | undefined>(undefined);
    const isItemSelected = (itemIndex: number) => selectedItem === itemIndex;
    const appInstance = useSelector(getAppInstance);
    const cornRecommendationEnabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
    const { productId } = useParams();
    const { open, toggle, setOpen, setClose } = useToggle();
    const hasWriteAccess = useHasWriteAccess();
    const recommendationOptions: RecommendationOption[] = [
        {
            icon: 'seedBag',
            title: _('Conocer cuál es el lote más adecuado para {{catalogName}}', {
                catalogName,
            }),
            description: _('Te recomendamos en qué lote conviene sembrar este híbrido según el ambiente.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'corn',
                        entry_source: 'product-exploration',
                    });
                }
            },
            onAcceptClick: () => {
                navigate(
                    reverse('densityRecommendation.DensityRecommendationCreate', {
                        qs: {
                            entrySource: 'product-exploration-detail',
                            type: 'corn_recommendation_view',
                            productId: Number(productId),
                        },
                    })
                );
            },
            hidden: !cornRecommendationEnabled,
            disabled: !hasPositioning,
            disabledTooltip: _('No puede generarse una recomendación del híbrido por el momento.'),
        },
        {
            icon: 'cornDensityRecommendation',
            title: _('Ajustar la densidad'),
            description: _('Calculamos la cantidad de semillas y las bolsas necesarias según el híbrido definido.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'density',
                        entry_source: 'product-exploration',
                    });
                }
            },
            onAcceptClick: () => {
                mpTrackEvent('Corn Density Recommendation began', {
                    entry_source: 'product-exploration',
                    product_id: Number(productId),
                });
                navigate(
                    reverse('densityRecommendation.DensityRecommendationCreate', {
                        qs: { entrySource: 'product-exploration-detail', productId: Number(productId) },
                    })
                );
            },
            hidden: !cornRecommendationEnabled,
            disabled: !hasDensityCoefficients,
        },
    ];
    const enabledRecommendations = recommendationOptions.filter((option) => !option.hidden);

    if (!enabledRecommendations.length || (!hasPositioning && !hasDensityCoefficients)) return null; // Don't show button if no recommendations are available

    return (
        <>
            {hasWriteAccess && (
                <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => {
                        mpTrackEvent('Generate Recommendation flow:Started', {
                            entry_source: 'product-exploration',
                            product_id: Number(productId),
                        });
                        toggle();
                    }}
                >
                    {_('Generar una recomendación')}
                </Button>
            )}
            <ModalV2
                title={_('Qué recomendación querés hacer?')}
                open={open}
                onClose={() => {
                    setSelectedItem(undefined);
                    toggle();
                }}
                maxWidth='md'
                bodyProps={{ alignItems: 'stretch', sx: { textAlign: 'left' } }}
                actionsProps={{ sx: { justifyContent: 'flex-end' } }}
                actions={
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={selectedItem === undefined}
                        onClick={
                            selectedItem !== undefined ? enabledRecommendations[selectedItem]?.onAcceptClick : undefined
                        }
                    >
                        {_('Aceptar')}
                    </Button>
                }
            >
                <Stack spacing={2} width='100%' p={1} maxWidth={'70vw'} sx={{ mx: 4 }}>
                    {enabledRecommendations.length === 0 ? (
                        <Stack spacing={1} textAlign='center'>
                            <Typography variant='h6' color='primary'>
                                {_('No hay opciones disponibles por el momento')}
                            </Typography>
                            <Typography variant='body2' color='primary'>
                                {_('Próximamente se agregarán opciones')}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction='row' flexWrap='nowrap' spacing={1}>
                            {enabledRecommendations.map((option, index) => (
                                <Stack key={`${option.icon}-${option.title}-${index}`} flexGrow={1}>
                                    <SelectionListItem
                                        leftItem={
                                            <Icon
                                                theme={settings.app.theme}
                                                type={option.icon}
                                                variant={option.isSelected(index) ? 'primary' : 'accent'}
                                            />
                                        }
                                        title={option.title}
                                        subtitle={option.description}
                                        rightItem={option.rightItem}
                                        isSelected={option.isSelected(index)}
                                        onClick={() => option.onClick(index)}
                                        disabled={option?.disabled || !option?.onAcceptClick}
                                        disabledTooltip={option?.disabledTooltip}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </ModalV2>
        </>
    );
};

const ProductExplorationDetail = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const units = useUnitProvider();
    const { type, slug, productId } = useParams();
    const productQuery = useCatalogProductById({ id: Number(productId), crop: type });
    useSaveLastViewedProduct({ type, slug, productId: Number(productId) });

    // Queries to check tabs data
    const { head, isValidating } = useH2HWrapper({ product: productQuery.data });
    const brands = useSelector((state) => getCatalogBrandsByCrop(state, 'crop_protection'));
    const simplifiedUseRecommendationsQuery = useCPSimplifiedUseRecommendations(
        {
            productId: Number(productId),
            page: 1,
            pageSize: 10000,
            brandIds: brands.map((brand) => brand.id),
        },
        {
            enabled: type === 'crop_protection',
        }
    );

    const [tab, setTab] = useState<TTab>('main');
    const tabs = useMemo(() => {
        return {
            main: {
                label: _('Información general'),
                component: <ProductExplorationGeneral product={productQuery.data} setTab={setTab} type={type} />,
                disabled: false,
                isLoading: false,
            },
            ...(type !== 'crop_protection' && {
                h2h: {
                    label: _('Comparar H2H'),
                    component: <ProductExplorationH2H product={productQuery.data} />,
                    disabled: !head,
                    isLoading: isValidating.environment || isValidating.results || isValidating.h2hOptions,
                },
            }),
            features: {
                label: _('Características'),
                component: <ProductExplorationFeatures product={productQuery.data} setTab={setTab} />,
                disabled: false,
                isLoading: false,
            },
            ...(type !== 'crop_protection' && {
                metrics: {
                    label: _('Perfil defensivo'),
                    component: <ProductExplorationMetrics product={productQuery.data} />,
                    disabled: productQuery.data?.metrics?.length === 0,
                    isLoading: false,
                },
            }),
            ...(type === 'crop_protection' && {
                whenToApply: {
                    label: _('Cuándo aplicar'),
                    component: (
                        <Stack>
                            <Typography variant='h6' color='primary'>
                                {_(
                                    'Consultá el momento ideal de aplicación según la problemática y el estado del cultivo.'
                                )}
                            </Typography>
                            <CropProtectionWhenToApply productId={Number(productId)} />
                        </Stack>
                    ),
                    disabled: simplifiedUseRecommendationsQuery.data?.results.length === 0,
                    isLoading: simplifiedUseRecommendationsQuery.isLoading,
                },
            }),
        };
    }, [productQuery.data, head, isValidating, type]);

    const { chips } = useMemo(() => {
        if (!productQuery.data) return { chips: [] };
        return parseProductForProductCard([productQuery.data], units, { largeChips: true })[0];
    }, [productQuery.data]);

    useEffect(() => {
        mpTrackEvent('Product Exploration Detail', {
            product_id: Number(productId),
            product_type: type as 'corn' | 'soy' | 'crop_protection',
        });
    }, [productId, type]);

    return (
        <Stack height='100%'>
            <Stack sx={{ background: 'linear-gradient(180deg, #FFF 0%, #F5F5F5 100%)' }}>
                <Container
                    maxWidth='xl'
                    sx={{
                        pt: 5,
                        pb: 2,
                    }}
                >
                    <Stack spacing={2}>
                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap='wrap'
                            useFlexGap
                            spacing={2}
                        >
                            <Breadcrumbs
                                breadcrumbs={[
                                    {
                                        label: _('Productos'),
                                        onClick: () => navigate(reverse('productExploration.main')),
                                    },
                                    {
                                        label: type === 'crop_protection' ? _('Protección de cultivos') : _('Híbridos'),
                                        onClick: () =>
                                            navigate(reverse('productExploration.list', { params: { slug, type } })),
                                    },
                                    { label: productQuery.data?.catalogName || productQuery.data?.name },
                                ]}
                            />

                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Box
                                    id={
                                        type === 'crop_protection'
                                            ? PRODUCT_EXPLORATION_TOUR_IDS.HELP_CP_DETAIL
                                            : PRODUCT_EXPLORATION_TOUR_IDS.HELP_HYBRID_DETAIL
                                    }
                                    component='span'
                                    sx={{ display: 'inline-flex' }}
                                />

                                <ProductRecommendation
                                    catalogName={productQuery.data?.catalogName}
                                    hasPositioning={!!productQuery.data?.rankings?.length}
                                    hasDensityCoefficients={!!productQuery.data?.densityCoefficients?.length}
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap={isMobile ? 'wrap' : 'nowrap'}
                            useFlexGap
                            spacing={2}
                        >
                            <Stack spacing={2}>
                                {productQuery.isValidating ? (
                                    <Skeleton variant='rectangular' width={55} height={55} />
                                ) : (
                                    productQuery.data?.brand?.logo && (
                                        <Box
                                            component='img'
                                            src={productQuery.data?.brand?.logo}
                                            height={'55px'}
                                            sx={{
                                                alignSelf: 'flex-start',
                                            }}
                                        />
                                    )
                                )}
                                <Stack spacing={1}>
                                    <Typography variant='overline' color='primary.mui.inactive'>
                                        {productQuery.isValidating ? (
                                            <Skeleton variant='text' />
                                        ) : type === 'crop_protection' ? (
                                            _(productQuery.data?.type)
                                        ) : (
                                            _('Semilla de maíz')
                                        )}
                                    </Typography>
                                    <Typography variant='h3' color='primary'>
                                        {productQuery.isValidating ? (
                                            <Skeleton variant='text' />
                                        ) : (
                                            productQuery.data?.catalogName || productQuery.data?.name
                                        )}
                                    </Typography>
                                    <Stack direction='row' spacing={1} mb={2} useFlexGap flexWrap='wrap'>
                                        {productQuery.isValidating
                                            ? [...Array(3)].map((_, index) => (
                                                  <Skeleton key={index} variant='text' width={100} />
                                              ))
                                            : (chips || []).map((chip) => {
                                                  return chip;
                                              })}
                                    </Stack>
                                    {(productQuery.data?.document?.length || 0) > 0 && (
                                        <Stack direction='row' spacing={1} mb={2} useFlexGap flexWrap='wrap'>
                                            {productQuery.data.document.map((docu, i) => {
                                                return (
                                                    <a
                                                        key={i}
                                                        href={docu.file}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        onClick={() => {
                                                            mpTrackEvent('Product Label Downloaded', {
                                                                product_id: Number(productId),
                                                            });
                                                        }}
                                                    >
                                                        <Button
                                                            variant='text'
                                                            color='secondary'
                                                            fullWidth
                                                            sx={{ mt: 2 }}
                                                            endIcon={<Download />}
                                                        >
                                                            {`${_('Descargar')} ${_(docu.name)}`}
                                                        </Button>
                                                    </a>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Stack>
                                <Typography variant='body1' color='primary'>
                                    {productQuery.isValidating ? (
                                        <Skeleton variant='text' />
                                    ) : (
                                        _(productQuery.data?.peShortDescription)
                                    )}
                                </Typography>
                            </Stack>
                            <Stack width={isMobile ? '100%' : 'auto'}>
                                {productQuery.isValidating ? (
                                    <Skeleton variant='rectangular' width={100} height={200} />
                                ) : (
                                    <Stack sx={{ width: 'max-content', m: 'auto' }}>
                                        <TechnologySlider
                                            data={(productQuery.data?.technologies || []).map((tec) => {
                                                return {
                                                    label: tec.id,
                                                    name: tec.name,
                                                    logoPath: tec.logoImage,
                                                    imgPath: tec.logos.find(
                                                        (i) => i.brandId === productQuery.data?.brand.id
                                                    )?.image,
                                                };
                                            })}
                                            onClick={() => {}}
                                            border={false}
                                            absolute={false}
                                            showTechnology={false}
                                            showArrows={false}
                                            imageHeight={200}
                                            hideIconWarning={true}
                                        />
                                        {productQuery.data?.image && (
                                            <Stack justifyContent='center' alignItems='center' sx={{ mx: { md: 24 } }}>
                                                <Box
                                                    component='img'
                                                    sx={{
                                                        height: '100%',
                                                        width: { xs: 100, md: 130 },
                                                    }}
                                                    alt={productQuery.data?.name}
                                                    src={productQuery.data?.image}
                                                />
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Container>
            </Stack>

            <PageContainer
                boxChildren={
                    <>
                        <Box
                            id={
                                type === 'crop_protection'
                                    ? PRODUCT_EXPLORATION_TOUR_IDS.CP_TABS
                                    : PRODUCT_EXPLORATION_TOUR_IDS.HYBRID_TABS
                            }
                            sx={{
                                width: '100%',
                            }}
                        >
                            <Tabs
                                value={tab}
                                onChange={(_, value) => setTab(value)}
                                centered
                                variant={isMobile ? 'scrollable' : 'fullWidth'}
                                sx={{
                                    width: '100%',
                                    minHeight: 56,
                                    '& .MuiTabs-flexContainer': {
                                        alignItems: 'stretch',
                                        minHeight: 56,
                                    },
                                }}
                            >
                                {Object.entries(tabs).map(([key, { label, disabled, isLoading }]) => (
                                    <Tab
                                        key={key}
                                        label={label}
                                        value={key}
                                        disabled={disabled || isLoading}
                                        icon={isLoading ? <CircularProgress size={12} /> : undefined}
                                        iconPosition='start'
                                        sx={{
                                            minHeight: 56,
                                            py: 1,
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        <Divider fullWidth />
                    </>
                }
            >
                {productQuery.isValidating ? (
                    <Stack py={{ xs: 1, sm: 5 }} alignItems='center' justifyContent='center' height='100%'>
                        <CircularProgress size={40} />
                    </Stack>
                ) : (
                    <Stack>{tabs[tab].component}</Stack>
                )}
            </PageContainer>
        </Stack>
    );
};

export default ProductExplorationDetail;
